import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

import { AuthProvider } from './contexts/AuthContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Farms from './pages/Farms';
import FarmDetail from './pages/FarmDetail';
import FarmCreate from './pages/FarmCreate';
import MapView from './pages/MapView';
import Analytics from './pages/Analytics';
import HistoricalAnalysis from './pages/HistoricalAnalysis';
import Alerts from './pages/Alerts';
import Settings from './pages/Settings';
import Login from './pages/Login';
import Register from './pages/Register';
import VRAMaps from './pages/VRAMaps';
import Diseases from './pages/Diseases';
import Irrigation from './pages/Irrigation';
import CropCalendar from './pages/CropCalendar';
import SoilHealth from './pages/SoilHealth';
import MarketPrices from './pages/MarketPrices';
import Cooperatives from './pages/Cooperatives';
import FarmSharing from './pages/FarmSharing';
import Equipment from './pages/Equipment';
import Weather from './pages/Weather';
import Notifications from './pages/Notifications';
import Finance from './pages/Finance';
import GeospatialTools from './pages/GeospatialTools';
import KnowledgeBase from './pages/KnowledgeBase';

// Admin pages
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import ApiConfig from './pages/admin/ApiConfig';
import UserManagement from './pages/admin/UserManagement';
import SystemMonitoring from './pages/admin/SystemMonitoring';
import AdminSettings from './pages/admin/AdminSettings';
import ActivityLogs from './pages/admin/ActivityLogs';
import Monitoring from './pages/admin/Monitoring';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <ThemeProvider>
            <LanguageProvider>
            <WebSocketProvider>
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />

                {/* Protected routes */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="farms" element={<Farms />} />
                  <Route path="farms/new" element={<FarmCreate />} />
                  <Route path="farms/:farmId" element={<FarmDetail />} />
                  <Route path="map" element={<MapView />} />
                  <Route path="analytics" element={<Analytics />} />
                  <Route path="history" element={<HistoricalAnalysis />} />
                  <Route path="alerts" element={<Alerts />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="vra" element={<VRAMaps />} />
                  <Route path="diseases" element={<Diseases />} />
                  <Route path="irrigation" element={<Irrigation />} />
                  <Route path="calendar" element={<CropCalendar />} />
                  <Route path="soil-health" element={<SoilHealth />} />
                  <Route path="market" element={<MarketPrices />} />
                  <Route path="cooperatives" element={<Cooperatives />} />
                  <Route path="sharing" element={<FarmSharing />} />
                  <Route path="equipment" element={<Equipment />} />
                  <Route path="weather" element={<Weather />} />
                  <Route path="notifications" element={<Notifications />} />
                  <Route path="finance" element={<Finance />} />
                  <Route path="geo-tools" element={<GeospatialTools />} />
                  <Route path="knowledge" element={<KnowledgeBase />} />
                </Route>

                {/* Admin routes */}
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <AdminLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<AdminDashboard />} />
                  <Route path="api-config" element={<ApiConfig />} />
                  <Route path="users" element={<UserManagement />} />
                  <Route path="monitoring" element={<SystemMonitoring />} />
                  <Route path="settings" element={<AdminSettings />} />
                  <Route path="logs" element={<ActivityLogs />} />
                  <Route path="observability" element={<Monitoring />} />
                </Route>

                {/* Catch all - redirect to login */}
                <Route path="*" element={<Navigate to="/login" replace />} />
              </Routes>
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: { background: '#333', color: '#fff' },
                }}
              />
            </WebSocketProvider>
            </LanguageProvider>
            </ThemeProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
